import { useCallback, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import useMediaQuery from "@mui/material/useMediaQuery";
import useEmblaCarousel from "embla-carousel-react";
import LicensesConst from "@consts/LicensesConst";
import logoWhite from "@images/logos/logoWhite.png";
import "./Pricing.scss";
import PricingCard from "@components/Pricing/PricingCard/PricingCard";
import GeneralStrings from "@strings/GeneralStrings.json";

const Pricing = () => {
  const pricingStrings = GeneralStrings.pricing;
  // Matches the app's $md-min breakpoint (screen-variables.scss) instead of user-agent sniffing.
  const isMobileViewport = !useMediaQuery("(min-width: 992px)");
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "start",
    breakpoints: {
      "(min-width: 992px) and (max-width: 1199px)": { slidesToScroll: 2 },
    },
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <section className="pricing" id="pricing">
      <div className="section pricing-background">
        <div className="pricing-container container">
          <div className="content">
            <div className="row">
              <div className="title-area">
                <h2>{pricingStrings.title}</h2>
                <div className="separator">
                  <img src={logoWhite} className="seperator-img" alt="logo" />
                </div>
                <p className="pricing-description">
                  {pricingStrings.description}
                </p>
                {isMobileViewport && (
                  <p className="mobile-pricing-description">
                    {GeneralStrings.mobilePricingDescription}
                  </p>
                )}
              </div>
            </div>

            <div className="row" id="tables">
              <div className="embla" ref={emblaRef}>
                <div className="embla__container">
                  {LicensesConst.LICENSES.map((license) => (
                    <div className="embla__slide" key={"slide-" + license.id}>
                      <PricingCard license={license} />
                    </div>
                  ))}
                </div>
              </div>
              {scrollSnaps.length > 1 && (
                <div className="embla__dots">
                  {scrollSnaps.map((_, index) => (
                    <button
                      key={index}
                      className={`embla__dot${index === selectedIndex ? " embla__dot--selected" : ""}`}
                      onClick={() => emblaApi?.scrollTo(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            <p
              className="bulk-deals-description"
              dangerouslySetInnerHTML={{
                __html: pricingStrings.bulkDealsHtml,
              }}
            />

            <div className="common-questions">
              <span className="common-questions__eyebrow">
                — Common Questions
              </span>
              <div className="common-questions__list">
                {GeneralStrings.commonQuestions.map((faq, index) => {
                  const panelId = `faq-panel-${index}`;
                  const isExpanded = expandedFaq === panelId;
                  return (
                    <Accordion
                      key={panelId}
                      expanded={isExpanded}
                      onChange={(_, expanded) =>
                        setExpandedFaq(expanded ? panelId : false)
                      }
                      disableGutters
                      elevation={0}
                    >
                      <AccordionSummary
                        expandIcon={isExpanded ? <CloseIcon /> : <AddIcon />}
                      >
                        <span className="faq-item__question">
                          {faq.question}
                        </span>
                      </AccordionSummary>
                      <AccordionDetails>
                        <p className="faq-item__answer">{faq.answer}</p>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
